import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {
    FunctionFeature,
    ISheetAPI,
    ISheetRange,
    ISheetRangeList,
    SheetType
} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';

export class StreamingFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'streaming';
    }

    @Function({
        title: 'START(range)',
        alias: 'START',
        example: 'START(A1:B1)',
        lazyOnly: true,
        tip: 'Start streams in the provided cells',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'start',
                tip: 'Start streams in the provided cells',
                type: Type.Boolean
            }
    })
    public async start(@Param({
                           title: 'sheetAPI',
                           tip: 'API',
                           type: SheetType.SheetAPI
                       })
                           api:ISheetAPI,
                       @Param({
                           title: 'range',
                           tip: 'Range to perform function in',
                           type: SheetType.SheetRangeList,
                           repeatable: true
                       }) rangeLists:ISheetRangeList[]):Promise<ABoolean>
    {
        const ctx = api.getSheetContext();

        for( let rangeList of rangeLists )
        {
            for( let range of rangeList.ranges )
            {
                if( !range )
                {
                    continue;
                }

                const rangeIterator = ctx.getRangeIterator(range);

                for( let offset = 0; offset < rangeIterator.numCells; offset++ )
                {
                    await ctx.startLiveCell(rangeIterator.getNthCellLocation(offset));
                }
            }
        }

        return ABoolean.true();
    }

    @Function({
        title: 'STOP(range)',
        alias: 'STOP',
        example: 'STOP(A1:B1)',
        lazyOnly: true,
        tip: 'Stop streams in the provided cells',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'stop',
                tip: 'Stop streams in the provided cells',
                type: Type.Boolean
            }
    })
    public async stop(@Param({
                          title: 'sheetAPI',
                          tip: 'API',
                          type: SheetType.SheetAPI
                      })
                          api:ISheetAPI,
                      @Param({
                          title: 'range',
                          tip: 'Range to perform function in',
                          type: SheetType.SheetRangeList,
                          repeatable: true
                      }) rangeLists:ISheetRangeList[]):Promise<ABoolean>
    {
        const ctx = api.getSheetContext();

        for( let rangeList of rangeLists )
        {
            for( let range of rangeList.ranges )
            {
                if( !range )
                {
                    continue;
                }

                const rangeIterator = ctx.getRangeIterator(range);

                for( let offset = 0; offset < rangeIterator.numCells; offset++ )
                {
                    await ctx.stopLiveCell(rangeIterator.getNthCellLocation(offset));
                }
            }
        }

        return ABoolean.true();
    }

    @Function({
        title: 'RESTART(range)',
        alias: 'RESTART',
        example: 'RESTART(A1:B1)',
        lazyOnly: true,
        tip: 'Restart streams in the provided cells',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'restart',
                tip: 'Restart streams in the provided cells',
                type: Type.Boolean
            }
    })
    public async restart(@Param({
                             title: 'sheetAPI',
                             tip: 'API',
                             type: SheetType.SheetAPI
                         })
                             api:ISheetAPI,
                         @Param({
                             title: 'range',
                             tip: 'Range to perform function in',
                             type: SheetType.SheetRangeList,
                             repeatable: true
                         }) rangeLists:ISheetRangeList[]):Promise<ABoolean>
    {
        const ctx = api.getSheetContext();

        for( let rangeList of rangeLists )
        {
            for( let range of rangeList.ranges )
            {
                if( !range )
                {
                    continue;
                }

                const rangeIterator = ctx.getRangeIterator(range);

                for( let offset = 0; offset < rangeIterator.numCells; offset++ )
                {
                    await ctx.restartLiveCell(rangeIterator.getNthCellLocation(offset));
                }
            }
        }

        return ABoolean.true();
    }

    @Function({
        title: 'isStreaming(range)',
        alias: 'IS_STREAMING',
        example: 'IS_STREAMING(A1:B1)',
        tip: 'Returns TRUE if any cell in a range is streaming. Otherwise returns FALSE',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                type: Type.Boolean,
                title: 'result',
                tip: 'Returns TRUE if any cell in a range is streaming. Otherwise returns FALSE'
            }
    })
    public isStreaming(@Param({
                           title: 'sheetAPI',
                           tip: 'API',
                           type: SheetType.SheetAPI
                       })
                           api:ISheetAPI,
                       @Param({
                           title: 'range',
                           tip: 'Range to perform function in',
                           type: SheetType.SheetRangeList,
                           repeatable: true
                       })
                           rangeLists:ISheetRangeList[]):IValue
    {
        const ctx = api.getSheetContext();

        return ABoolean.fromBoolean(rangeLists.some((rangeList:ISheetRangeList) =>
        {
            return rangeList.ranges.some((range:ISheetRange) =>
            {
                if( range )
                {
                    const rangeIterator = ctx.getRangeIterator(range);
                    const numCells = rangeIterator.numCells;

                    for( let cellOffset = 0; cellOffset < numCells; cellOffset++ )
                    {
                        if( ctx.isCellStreaming(rangeIterator.getNthCellLocation(cellOffset)) )
                        {
                            return true;
                        }
                    }
                }

                return false;
            });
        }));
    }
}
